import { useState, useEffect, useMemo } from "react";

interface MesRendimiento {
  id: string;
  period_month: string; // ISO date string, e.g. '2024-09-01'
  value: number;
  total_acquisition_cost: number;
  total_asset_value: number;
  annual_rents: number;
  cash: number;
  equivalents: number;
  mortgages: number;
  bullet_loans: number;
}

interface EvolutionChartProps {
  meses: MesRendimiento[];
}

interface TooltipProps {
  visible: boolean;
  x: number;
  y: number;
  data: MesRendimiento | null;
}

// Elegant Point component with smooth interactions
interface PointProps {
  cx: number;
  cy: number;
  data: MesRendimiento;
  onHover: (data: MesRendimiento, event: React.MouseEvent) => void;
  onMove: (event: React.MouseEvent) => void;
  onLeave: () => void;
  isHovered: boolean;
}

function Point({ cx, cy, data, onHover, onMove, onLeave, isHovered }: PointProps) {
  return (
    <g className="transition-all duration-300 ease-out">
      {/* Base point - subtle presence */}
      <circle
        cx={cx}
        cy={cy}
        r={isHovered ? 0 : 2}
        fill="#16A34A"
        className="transition-all duration-300 ease-out"
        style={{ opacity: isHovered ? 0 : 0.8 }}
      />
      
      {/* Hover state - elegant glow effect */}
      {isHovered && (
        <>
          {/* Outer glow */}
          <circle
            cx={cx}
            cy={cy}
            r={8}
            fill="#16A34A"
            opacity={0.1}
            className="animate-pulse"
          />
          {/* Main hover point */}
          <circle
            cx={cx}
            cy={cy}
            r={4}
            fill="#16A34A"
            className="transition-all duration-300 ease-out"
            style={{
              filter: 'drop-shadow(0 0 12px rgba(22, 163, 74, 0.4))'
            }}
          />
          {/* Inner highlight */}
          <circle
            cx={cx}
            cy={cy}
            r={1.5}
            fill="rgba(255, 255, 255, 0.9)"
            className="transition-all duration-300 ease-out"
          />
        </>
      )}
      
      {/* Invisible hover area */}
      <circle
        cx={cx}
        cy={cy}
        r={16}
        fill="transparent"
        className="cursor-pointer"
        onMouseEnter={(e) => onHover(data, e)}
        onMouseMove={onMove}
        onMouseLeave={onLeave}
      />
    </g>
  );
}

// Premium Tooltip component
function Tooltip({ visible, x, y, data }: TooltipProps) {
  if (!visible || !data) return null;

  const formatValue = (value: number) => {
    const rounded = Math.round(value);
    return `€${rounded.toLocaleString('es-ES')}`;
  };

  const formatPeriodMonth = (isoDate?: string) => {
    if (!isoDate) return "";
    // Avoid Date parsing due to timezone shifts; parse YYYY-MM safely
    const ym = String(isoDate).slice(0, 7);
    const [yyyy, mm] = ym.split('-');
    const year = Number(yyyy) || NaN;
    const monthIdx = Number(mm) ? Number(mm) - 1 : NaN;
    if (!Number.isFinite(year) || !Number.isFinite(monthIdx) || monthIdx < 0 || monthIdx > 11) return "";
    const monthNamesLocal = [
      "enero",
      "febrero",
      "marzo",
      "abril",
      "mayo",
      "junio",
      "julio",
      "agosto",
      "septiembre",
      "octubre",
      "noviembre",
      "diciembre",
    ];
    const month = monthNamesLocal[monthIdx] || "";
    // Capitalize first letter to match other labels
    return `${month.charAt(0).toUpperCase()}${month.slice(1)} ${year}`;
  };

  return (
    <div
      className="fixed z-[9999] pointer-events-none"
      style={{
        left: x,
        top: y,
        transform: y > 100 ? 'translate(-50%, -100%)' : 'translate(-50%, 0%)'
      }}
    >
      <div
        className="relative px-6 py-4 rounded-xl shadow-2xl backdrop-blur-md border border-white/20"
        style={{
          background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.97) 0%, rgba(30, 41, 59, 0.97) 100%)',
          animation: 'tooltipFadeIn 150ms cubic-bezier(0.25, 0.46, 0.45, 0.94)',
          boxShadow: '0 25px 50px rgba(0, 0, 0, 0.35), 0 10px 20px rgba(0, 0, 0, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.12)',
          minWidth: '140px'
        }}
      >
        <div className="text-xs font-medium text-gray-300 mb-2.5 opacity-95 leading-tight tracking-wide">
          {formatPeriodMonth(data.period_month)}
        </div>
        <div 
          className="font-bold text-white text-xl tracking-tight"
          style={{ 
            fontFeatureSettings: '"tnum"',
            letterSpacing: '-0.03em'
          }}
        >
          {formatValue(data.value)}
        </div>
        
        {/* Tooltip Arrow Premium - Dynamic */}
        <div
          className={`absolute left-1/2 transform -translate-x-1/2 ${
            y > 100 ? 'top-full' : 'bottom-full'
          }`}
          style={{
            width: 0,
            height: 0,
            borderLeft: '9px solid transparent',
            borderRight: '9px solid transparent',
            ...(y > 100 
              ? { borderTop: '9px solid rgba(15, 23, 42, 0.97)' }
              : { borderBottom: '9px solid rgba(15, 23, 42, 0.97)' }
            ),
            filter: 'drop-shadow(0 3px 6px rgba(0, 0, 0, 0.25))'
          }}
        />
        
        {/* Subtle glow effect */}
        <div 
          className="absolute inset-0 rounded-xl"
          style={{
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, transparent 60%)',
            pointerEvents: 'none'
          }}
        />
      </div>
    </div>
  );
}

export function EvolutionChart({ meses }: EvolutionChartProps) {
  const [hoveredPoint, setHoveredPoint] = useState<MesRendimiento | null>(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  const [isAnimated, setIsAnimated] = useState(false);
  const [selectedYear, setSelectedYear] = useState<number | 'all'>('all');

  // Slow, elegant animation trigger — keep hooks at top-level
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsAnimated(true);
    }, 200); // Slightly longer delay for dramatic effect
    return () => clearTimeout(timer);
  }, []);
  // Helper: parse YYYY-MM from ISO-ish strings safely
  const parseYm = (iso?: string) => {
    if (!iso) return null;
    const ym = String(iso).slice(0, 7);
    const [y, m] = ym.split('-').map(Number);
    if (!Number.isFinite(y) || !Number.isFinite(m)) return null;
    return new Date(y, m - 1, 1);
  };

  // Allow quick UI testing by adding N months before the first registro
  // Use the provided `meses` directly. Removed development `extendMonths` query hack.
  const sourceMeses = meses;

  // Build an array of month-start Dates covering from the earliest
  // registro in `sourceMeses` up to the latest registro (inclusive).
  // If `sourceMeses` is empty, fall back to the last 12 months.
  const buildMonthsFromData = () => {
    // If there are no source months, return an empty array (no 12-month fallback)
    if (!sourceMeses || sourceMeses.length === 0) return [];

    // Find earliest and latest
    let earliest: Date | null = null;
    let latest: Date | null = null;
    for (const m of sourceMeses) {
      const d = parseYm(m.period_month);
      if (!d) continue;
      if (!earliest || d < earliest) earliest = d;
      if (!latest || d > latest) latest = d;
    }

    // If we couldn't determine a valid range from the data, return empty
    if (!earliest || !latest) return [];

    const out: Date[] = [];
    const cursor = new Date(earliest.getFullYear(), earliest.getMonth(), 1);
    const end = new Date(latest.getFullYear(), latest.getMonth(), 1);
    while (cursor <= end) {
      out.push(new Date(cursor.getFullYear(), cursor.getMonth(), 1));
      // advance one month
      cursor.setMonth(cursor.getMonth() + 1);
    }
    return out;
  };

  const monthsDates = buildMonthsFromData();

  const availableYears = useMemo(() => {
    if (!sourceMeses || sourceMeses.length === 0) return [] as number[];
    const years = Array.from(new Set(sourceMeses.map(m => {
      const d = parseYm(m.period_month);
      return d ? d.getFullYear() : null;
    }).filter((y): y is number => y !== null))).sort((a,b) => a - b);
    return years;
  }, [sourceMeses]);

  useEffect(() => {
    if (selectedYear !== 'all' && !availableYears.includes(selectedYear as number)) {
      setSelectedYear('all');
    }
  }, [availableYears, selectedYear]);

  const monthsDatesFiltered = selectedYear === 'all' ? monthsDates : monthsDates.filter(d => d.getFullYear() === selectedYear);

  // Helpers to produce month keys in `YYYY-MM` format avoiding timezone issues
  const pad = (n: number) => String(n).padStart(2, '0');
  const monthKeyFromDate = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}`;
  const monthKeyFromIso = (iso?: string) => {
    if (!iso) return '';
    // ISO may be "YYYY-MM-DD" or "YYYY-MM-DDTHH:MM:SSZ" — take first 7 chars
    return iso.slice(0, 7);
  };

  const findByMonth = (d: Date) => {
    const key = monthKeyFromDate(d);
    return sourceMeses.find(m => monthKeyFromIso(m.period_month) === key);
  };

  // Build monthsData but carry-forward last known valuation when a month is missing
  const monthsData: MesRendimiento[] = (() => {
    const out: MesRendimiento[] = [];
    for (const d of monthsDatesFiltered) {
      const found = findByMonth(d);
      if (found) {
        out.push(found);
        continue;
      }

      const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
      const prev = out.length > 0 ? out[out.length - 1] : null;

      out.push({
        id: `month-${iso}`,
        period_month: iso,
        value: prev ? prev.value : 0,
        total_acquisition_cost: prev ? prev.total_acquisition_cost : 0,
        total_asset_value: prev ? prev.total_asset_value : 0,
        annual_rents: prev ? prev.annual_rents : 0,
        cash: prev ? prev.cash : 0,
        equivalents: prev ? prev.equivalents : 0,
        mortgages: prev ? prev.mortgages : 0,
        bullet_loans: prev ? prev.bullet_loans : 0,
      });
    }
    return out;
  })();

  // Keep the rest of the component working by assigning monthsData to
  // `validMeses` so existing logic remains unchanged.
  const validMeses = monthsData;

  const monthNames = [
    "Enero",
    "Febrero",
    "Marzo",
    "Abril",
    "Mayo",
    "Junio",
    "Julio",
    "Agosto",
    "Septiembre",
    "Octubre",
    "Noviembre",
    "Diciembre",
  ];

  const formatPeriodMonth = (isoDate?: string) => {
    if (!isoDate) return "";
    // Avoid using `new Date(isoDate)` which can shift the day/month due to timezone.
    // Support values like 'YYYY-MM', 'YYYY-MM-DD' or full timestamps.
    const ym = String(isoDate).slice(0, 7); // 'YYYY-MM'
    const [yyyy, mm] = ym.split('-');
    const year = Number(yyyy) || NaN;
    const monthIdx = Number(mm) ? Number(mm) - 1 : NaN;
    if (!Number.isFinite(year) || !Number.isFinite(monthIdx) || monthIdx < 0 || monthIdx > 11) return "";
    const month = monthNames[monthIdx] || "";
    return `${month} ${year}`;
  };

  // Premium chart dimensions with generous spacing
  const chartHeight = 300;
  const chartWidth = 800;
  const margin = { top: 30, right: 40, bottom: 50, left: 90 };
  
  const plotWidth = chartWidth - margin.left - margin.right;
  const plotHeight = chartHeight - margin.top - margin.bottom;

  // Note: we intentionally render the chart for the last 12 months even if
  // all values are zero. `validMeses` (backed by `monthsData`) contains
  // the full historical monthly range from the first registro to the last.

  // Calculate value range with padding for elegance. Guard against empty data.
  const maxValue = validMeses.length ? Math.max(...validMeses.map(d => d.value)) : 0;
  const minValue = validMeses.length ? Math.min(...validMeses.map(d => d.value)) : 0;
  const valueRange = maxValue - minValue;

  const getPointPosition = (value: number, index: number) => {
    // Normalize value to 0-1 range (0 = minValue, 1 = maxValue)
    const normalizedValue = valueRange > 0 
      ? (value - minValue) / valueRange 
      : 0;
    // Handle single point case
    const xPosition = validMeses.length > 1
      ? (index / (validMeses.length - 1)) * plotWidth
      : plotWidth / 2; // Center single point
    const x = xPosition + margin.left;
    // Y coordinate: higher values go to top, lower values to bottom
    const y = margin.top + plotHeight - (normalizedValue * plotHeight);
    return { x, y };
  };

  // Create elegant smooth curve
  const createSmoothPath = (points: { x: number; y: number }[]) => {
    if (points.length < 2) return '';
    
    let path = `M ${points[0].x} ${points[0].y}`;
    
    for (let i = 1; i < points.length; i++) {
      const prev = points[i - 1];
      const curr = points[i];
      const smoothing = 0.25; // Smooth but not too curved
      
      const cpx1 = prev.x + (curr.x - prev.x) * smoothing;
      const cpy1 = prev.y;
      const cpx2 = curr.x - (curr.x - prev.x) * smoothing;
      const cpy2 = curr.y;
      
      path += ` C ${cpx1} ${cpy1}, ${cpx2} ${cpy2}, ${curr.x} ${curr.y}`;
    }
    
    return path;
  };

  const points = validMeses.map((d, i) => getPointPosition(d.value, i));
  const pathData = createSmoothPath(points);

  // Elegant area under curve
  const areaPath = pathData + ` L ${margin.left + plotWidth} ${margin.top + plotHeight} L ${margin.left} ${margin.top + plotHeight} Z`;

  // Y-axis ticks with smart formatting (bottom to top)
  const yTicks = Array.from({ length: 5 }, (_, i) => {
    const value = minValue + (valueRange * i) / 4;
    return { 
      value, 
      label: value >= 1000 ? `€${Math.round(value / 1000)}K` : `€${Math.round(value)}`
    };
  });

  const handlePointHover = (data: MesRendimiento, event: React.MouseEvent) => {
    setHoveredPoint(data);
    updateTooltipPosition(event);
  };

  const handlePointMove = (event: React.MouseEvent) => {
    if (hoveredPoint) {
      updateTooltipPosition(event);
    }
  };

  const updateTooltipPosition = (event: React.MouseEvent) => {
    const tooltipHeight = 80; // Altura estimada del tooltip
    const margin = 16; // Margen desde el cursor
    
    // Detectar si el tooltip se cortaría en la parte superior
    const wouldClipTop = event.clientY - tooltipHeight - margin < 20;
    
    setTooltipPos({
      x: event.clientX,
      y: wouldClipTop 
        ? event.clientY + margin // Mostrar debajo del cursor si se cortaría arriba
        : event.clientY - margin // Mostrar arriba del cursor normalmente
    });
  };

  const handlePointLeave = () => {
    setHoveredPoint(null);
  };

  // Calculate performance metrics
  const startValue = validMeses[0]?.value || 0;
  const endValue = validMeses[validMeses.length - 1]?.value || 0;
  const totalGrowth = startValue > 0 ? ((endValue - startValue) / startValue) * 100 : 0;

  return (
    <>
      <div 
        className="bg-white border border-[#E8EDF4] rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-all duration-500"
      >
        {/* Elegant header */}
        <div className="p-6 pb-4 border-b border-gray-50">
            <div className="flex items-start justify-between">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Evolución VALUE
              </h3>
              <div className="flex items-center gap-3">
                <label className="text-sm text-gray-600">Año</label>
                <select
                  value={selectedYear === 'all' ? 'all' : String(selectedYear)}
                  onChange={(e) => setSelectedYear(e.target.value === 'all' ? 'all' : Number(e.target.value))}
                  className="border rounded px-2 py-1 text-sm"
                >
                  <option value="all">Todos</option>
                  {availableYears.map(y => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex items-center gap-6 text-sm text-gray-600 flex-wrap">
              <span className="flex items-center gap-2">
                <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                Desde {formatPeriodMonth(validMeses[0]?.period_month) || 'Enero 2024'}
              </span>
              <span className="inline-flex items-center gap-2">
                <span className="text-[#16A34A] font-semibold text-base">
                  {totalGrowth >= 0 ? '+' : ''}{totalGrowth.toFixed(1)}%
                </span>
                <span className="text-gray-500">total</span>
              </span>
            </div>
        </div>

        {/* Chart Container - full-width but maintain aspect ratio */}
        <div
          className="relative w-full p-6 pt-2"
          style={{ width: '100%', aspectRatio: `${chartWidth} / ${chartHeight}` }}
        >
          <svg
            width="100%"
            height="100%"
            viewBox={`0 0 ${chartWidth} ${chartHeight}`}
            className="w-full h-full"
            preserveAspectRatio="xMidYMid meet"
          >
            <defs>
              {/* Slow animation mask */}
              <mask id="line-mask-evolution">
                <rect 
                  x="0" 
                  y="0" 
                  width={isAnimated ? "100%" : "0%"} 
                  height="100%" 
                  fill="white"
                  style={{
                    transition: 'width 1200ms cubic-bezier(0.4, 0, 0.2, 1)' // Much slower, elegant easing
                  }}
                />
              </mask>
              
              {/* Elegant gradient for area */}
              <linearGradient id="areaGradientEvolution" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#16A34A" stopOpacity="0.12" />
                <stop offset="100%" stopColor="#16A34A" stopOpacity="0.02" />
              </linearGradient>
            </defs>
            
            {/* Subtle horizontal grid lines */}
            {yTicks.map((_, i) => {
              // Grid lines from bottom to top (i=0 at bottom, i=4 at top)
              const y = margin.top + plotHeight - (i * (plotHeight / 4));
              return (
                <line
                  key={i}
                  x1={margin.left}
                  y1={y}
                  x2={margin.left + plotWidth}
                  y2={y}
                  stroke="#F1F5F9"
                  strokeWidth="1"
                  opacity="0.8"
                />
              );
            })}
            
            {/* Elegant area under line */}
            <path
              d={areaPath}
              fill="url(#areaGradientEvolution)"
              mask="url(#line-mask-evolution)"
              className="transition-all duration-1200 ease-out"
            />
            
            {/* Main line - premium styling */}
            <path
              d={pathData}
              fill="none"
              stroke="#16A34A"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              mask="url(#line-mask-evolution)"
              style={{
                filter: 'drop-shadow(0 1px 3px rgba(22, 163, 74, 0.1))'
              }}
            />
            
            {/* Interactive data points */}
            {validMeses.map((d, i) => {
              const pos = getPointPosition(d.value, i);
              return (
                <Point
                  key={d.id}
                  cx={pos.x}
                  cy={pos.y}
                  data={d}
                  onHover={handlePointHover}
                  onMove={handlePointMove}
                  onLeave={handlePointLeave}
                  isHovered={hoveredPoint === d}
                />
              );
            })}
            
            {/* Y-axis labels - elegant typography */}
            {yTicks.map((tick, i) => {
              // Y positions: bottom to top (i=0 at bottom, i=4 at top)
              const y = margin.top + plotHeight - (i * (plotHeight / 4));
              return (
                <text
                  key={i}
                  x={margin.left - 16}
                  y={y}
                  textAnchor="end"
                  dominantBaseline="middle"
                  style={{ 
                    fontSize: '12px', 
                    fontWeight: '500', 
                    fill: '#64748B',
                    fontFamily: 'Inter, sans-serif',
                    fontFeatureSettings: '"tnum"'
                  }}
                >
                  {tick.label}
                </text>
              );
            })}
            
            {/* X-axis labels - refined spacing */}
            {validMeses.map((d, i) => {
              if (i % Math.max(1, Math.floor(validMeses.length / 6)) !== 0 && i !== validMeses.length - 1) return null;
              const pos = getPointPosition(d.value, i);
              // Obtener solo el nombre del mes (abreviado)
              const monthLabel = (formatPeriodMonth(d.period_month).split(' ')[0] || '').substring(0, 3);
              return (
                <text
                  key={d.id}
                  x={pos.x}
                  y={chartHeight - margin.bottom + 24}
                  textAnchor="middle"
                  style={{
                    fontSize: '12px',
                    fontWeight: '500',
                    fill: '#64748B',
                    fontFamily: 'Inter, sans-serif'
                  }}
                >
                  {monthLabel}
                </text>
              );
            })}
          </svg>
        </div>
      </div>

      {/* Premium Tooltip que sigue el cursor exactamente - Fuera del contenedor */}
      <Tooltip
        visible={hoveredPoint !== null}
        x={tooltipPos.x}
        y={tooltipPos.y}
        data={hoveredPoint}
      />
    </>
  );
}